import { Badge, Card, CardContent, CardHeader, CardTitle } from "@repo/ui";
import { labelCategorieFitCulturel } from "@repo/utils";
import type { ConfigReferentiel } from "../../../../convex/referentiel/validation";

type Props = {
  config: ConfigReferentiel;
};

/**
 * Items (dimensions) du référentiel de la campagne.
 *
 * Le SNAPSHOT de la campagne, pas la banque d'organisation : renommer un item de
 * la banque ne change rien ici, et c'est voulu (§3.3). Cette liste est donc la
 * seule réponse fiable à « sur quoi les référents se sont-ils prononcés ? » —
 * indispensable une fois le référentiel verrouillé, où l'écran de configuration
 * n'est plus accessible.
 *
 * L'ordre est celui de la config : pour un Q-sort c'est l'ordre de sélection de
 * l'admin, pour un PAMI celui des 20 dimensions du test.
 */
export function ReferentielItemsCard({ config }: Props) {
  const estQsort = config.echelle.kind === "qsort";

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex flex-wrap items-center gap-3 text-base">
          {estQsort ? "Items classés par les référents" : "Dimensions évaluées"}
          <Badge variant="outline">{config.dimensions.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {estQsort
            ? "Référents et candidats classent exactement ces items, avec une consigne différente."
            : "Les référents indiquent une plage de score attendue sur chacune de ces dimensions."}
        </p>

        <ol className="divide-y rounded-surface border text-sm">
          {config.dimensions.map((dimension, i) => (
            <li
              key={dimension.code}
              className="flex items-start gap-3 p-3 leading-snug"
            >
              <span className="w-6 shrink-0 text-right text-muted-foreground tabular-nums">
                {i + 1}
              </span>
              <span className="flex-1">
                {dimension.label}
                {dimension.description && (
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {dimension.description}
                  </span>
                )}
              </span>
              <span className="flex shrink-0 flex-wrap justify-end gap-1">
                {dimension.categorie && (
                  <Badge variant="outline">
                    {labelCategorieFitCulturel(dimension.categorie) ??
                      dimension.categorie}
                  </Badge>
                )}
                {dimension.origine === "custom" && (
                  <Badge variant="purple">personnalisé</Badge>
                )}
              </span>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}
